import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Volume2, VolumeX, Play, Check, MessageSquare } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import type { NotificationSound } from "@shared/schema";

type SoundType = 
  | 'soundPulseNotifications'
  | 'soundUnreadChats'
  | 'soundMentions'
  | 'soundEmailMatches'
  | 'soundAdminNotifications'
  | 'soundSecurityNotifications'
  | 'soundActiveCalls'
  | 'soundScheduledCalls';

interface SoundSelectorProps {
  soundType: SoundType;
  label: string;
  className?: string;
}

interface AudioSettings {
  audioNotificationsEnabled: boolean;
  soundPulseNotifications: NotificationSound | null;
  soundUnreadChats: NotificationSound | null;
  soundMentions: NotificationSound | null;
  soundEmailMatches: NotificationSound | null;
  soundAdminNotifications: NotificationSound | null;
  soundSecurityNotifications: NotificationSound | null;
  soundActiveCalls: NotificationSound | null;
  soundScheduledCalls: NotificationSound | null;
  availableSounds: NotificationSound[];
}

export function SoundSelector({ soundType, label, className = '' }: SoundSelectorProps) {
  const { toast } = useToast();

  const { data: settings, isLoading } = useQuery<AudioSettings>({
    queryKey: ['/api/user/audio-settings'],
  });

  const updateMutation = useMutation({
    mutationFn: async (soundId: number | null) => {
      await apiRequest('PATCH', '/api/user/audio-settings', {
        [soundType]: soundId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/user/audio-settings'] });
      toast({
        title: "Sound updated",
        description: "Notification sound preference saved",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update sound",
        variant: "destructive",
      });
    },
  });

  const currentSound = settings?.[soundType] as NotificationSound | null;
  const sounds = settings?.availableSounds || [];

  const playPreview = (sound: NotificationSound, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!sound.directUrl) return;
    
    const audio = new Audio(sound.directUrl);
    audio.play().catch(() => {});
  };

  const handleSelect = (soundId: number | null) => {
    updateMutation.mutate(soundId);
  };

  if (isLoading || !settings) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`h-auto p-1 text-xs gap-1 hover:bg-gray-700 ${className}`}
          data-testid={`sound-selector-${soundType}`}
        >
          {currentSound ? (
            <Volume2 className="h-3 w-3 text-blue-400" />
          ) : (
            <VolumeX className="h-3 w-3 text-gray-500" />
          )}
          <span className="text-gray-400 hover:text-white">{label}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="start" 
        className="w-80 max-w-[90vw] bg-gray-800 border-gray-600 z-[70]"
        data-testid={`sound-dropdown-${soundType}`}
      >
        <DropdownMenuItem
          onClick={() => handleSelect(null)}
          className="flex items-center justify-between hover:bg-gray-700 cursor-pointer"
          data-testid={`sound-option-none-${soundType}`}
        >
          <span className="flex items-center gap-2">
            <VolumeX className="h-4 w-4 text-gray-500" />
            None (Silent)
          </span>
          {!currentSound && <Check className="h-4 w-4 text-blue-400" />}
        </DropdownMenuItem>
        
        {sounds.length > 0 && <DropdownMenuSeparator className="bg-gray-600" />}
        
        {sounds.map((sound) => (
          <DropdownMenuItem
            key={sound.id}
            onClick={() => handleSelect(sound.id)}
            className="flex items-center justify-between hover:bg-gray-700 cursor-pointer"
            data-testid={`sound-option-${sound.id}-${soundType}`}
          >
            <span className="flex items-center gap-2 min-w-0 flex-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-5 w-5 p-0 hover:bg-gray-600 flex-shrink-0"
                onClick={(e) => playPreview(sound, e)}
                data-testid={`sound-preview-${sound.id}-${soundType}`}
              >
                <Play className="h-3 w-3" />
              </Button>
              <span className="truncate" title={sound.name}>{sound.name}</span>
            </span>
            {currentSound?.id === sound.id && <Check className="h-4 w-4 text-blue-400" />}
          </DropdownMenuItem>
        ))}
        
        {sounds.length === 0 && (
          <div className="px-2 py-3 text-sm text-gray-400 text-center">
            No sounds available.<br />
            <span className="text-xs">Admin can upload sounds in Settings.</span>
          </div>
        )}

        <DropdownMenuSeparator className="bg-gray-600" />
        
        <DropdownMenuItem
          disabled
          className="flex items-center gap-2 text-gray-500 opacity-50 cursor-not-allowed"
          data-testid={`sms-placeholder-${soundType}`}
        >
          <MessageSquare className="h-4 w-4" />
          Notify by SMS
          <span className="ml-auto text-xs">(Coming soon)</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
